/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { ModuleRuleValidationResult } from 'common-typescript';
import { EntityWithRule, ModuleRule, Rule } from 'common-typescript/types';
import { RULE_SERVICE } from '../../../ajs-upgraded-modules';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../../plan/plan-actions-service/plan-actions.service';
import { PlanRuleSelectionService } from '../../../service/plan-rule-selection.service';
import { getRuleValidationResultFromState } from '../../../service/rule-validation-result-in-state';
import { PlanStructureContext } from '../../plan-structure-context';

@Component({
    selector: 'sis-plan-structure-module-rule',
    templateUrl: './plan-structure-module-rule.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureModuleRuleComponent {
    rule = input.required<ModuleRule>();
    parentModule = input.required<EntityWithRule>();
    selectionUIState = input<'ACTIVE' | 'DISABLED' | 'SELECTABLE'>('ACTIVE');
    ruleDepthLevel = input.required<number>();
    headingLevel = input.required<number>();

    ruleService = inject(RULE_SERVICE);
    planRuleSelectionService: PlanRuleSelectionService = inject(PlanRuleSelectionService);
    planActionsService: PlanActionsService = inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN);
    private readonly planStructureContext = inject(PlanStructureContext);

    ruleValidationResult = computed(this.ruleValidationResultComputation());
    ruleModule = computed(this.ruleModuleComputation());
    parentRule = computed<Rule | null>(this.parentRuleComputation());
    isParentRuleSelectAllRule = computed<boolean>(this.isParentRuleSelectAllRuleComputation());
    isParentSelectOneRule = computed<boolean>(this.isSelectOneRuleComputation());

    isRuleSelectionDisabled = computed(this.isRuleSelectionDisabledComputation());
    showMandatorySelection = computed(this.showMandatorySelectionComputation());
    isGroupingModuleMandatory = computed(this.isGroupingModuleMandatoryComputation());

    action = computed(this.actionComputation());
    actionInputType = computed(this.actionInputTypeComputation());
    actionInputName = computed(this.actionInputNameComputation());

    handleToggle(): void {
        const active = this.ruleValidationResult().active;
        const module = this.ruleModule();
        const parentModule = this.parentModule();
        if (!active) {
            this.planActionsService?.selectModule(module, parentModule);
        } else {
            this.planActionsService?.removeModule(module, parentModule);
        }
    }

    ruleValidationResultComputation(): () => ModuleRuleValidationResult {
        return () => getRuleValidationResultFromState(this.planStructureContext.planStateObject(), this.parentModule(), this.rule());
    }

    ruleModuleComputation(): () => EntityWithRule {
        return () => this.planStructureContext.planRuleData().ruleModuleVersionsByGroupId[this.rule().moduleGroupId];
    }

    parentRuleComputation(): () => Rule | null {
        return () => this.ruleService.getParentRule(this.parentModule().rule, this.rule());
    }

    isParentRuleSelectAllRuleComputation(): () => boolean {
        return () => this.ruleService.isSelectAllRule(this.parentRule());
    }

    isRuleSelectionDisabledComputation(): () => boolean {
        return () =>
            this.selectionUIState() === 'DISABLED' ||
            (this.isParentRuleSelectAllRule() && this.ruleValidationResult()?.active) ||
            this.planRuleSelectionService.isModuleSelectionDisabled(
                this.ruleModule(),
                this.parentModule(),
                this.planStructureContext.validatablePlan(),
            );
    }

    isSelectOneRuleComputation(): () => boolean {
        return () => this.ruleService.isSelectOneRule(this.parentRule());
    }

    showMandatorySelectionComputation() {
        return () => this.ruleValidationResult()?.active && (this.isParentRuleSelectAllRule() || !this.parentRule());
    }

    isGroupingModuleMandatoryComputation(): () => boolean {
        return () => this.isParentRuleSelectAllRule() || !this.parentRule();
    }

    actionComputation() {
        return () => (this.isRuleSelectionDisabled() ? undefined : ('select-to-plan' as const));
    }

    actionInputTypeComputation() {
        return () => (this.isParentSelectOneRule() ? 'radio-button' : 'checkbox');
    }

    actionInputNameComputation() {
        return () => (this.parentRule() ? this.parentRule().localId : this.rule().localId);
    }
}
